import { IMultiCallItem, IStatus } from "@/lib/interfaces";
import { appApi } from "../../appApi";
import { ObjectTypeAPI } from "../../baseApi";
import { passGetQuery } from "../functions";

export interface IAPISimpleResponse {
  status: IStatus;
}

export interface AgnosticParams {
  findOne?: boolean;
  filters?: Array<{
    field: string;
    value: string | number;
    operator?: string;
  }>;
  [key: string]:
    | string
    | number
    | boolean
    | Array<{ field: string; value: string | number; operator?: string }>
    | undefined;
}

export interface IAgnosticGetQuery {
  url: string;
  params?: AgnosticParams;
}

export interface IAgnosticDeleteQuery {
  url: string;
  id: string;
  suffix?: string;
  body?: {
    [key: string]: string | number;
  };
}

export interface IAgnosticPostQuery {
  url?: string;
  body?: {
    [key: string]: string | number;
  };
  method?: "POST" | "PUT" | "DELETE";
}

export const agnosticAPI = appApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getUser: builder.query<any, string>({
      query: (id) => ({
        url: `/user/${id}`,
        method: "get",
      }),
    }),
    //GET ALL, without tagging for refetch
    agnosticUntaggedGet: builder.query<any, IAgnosticGetQuery>({
      query: ({ url, params }) => ({
        url:
          !params || Object.values(params).length === 0
            ? url
            : `${url}?data=${passGetQuery({ params })}`,
        method: "get",
      }),
    }),
    //GET ALL
    agnosticGet: builder.query<any, IAgnosticGetQuery>({
      query: ({ url, params }) => ({
        url:
          !params || Object.values(params).length === 0
            ? url
            : `${url}?data=${passGetQuery({ params })}`,
        method: "get",
      }),
      providesTags: (result) => {
        const tags = result?.tagsProvided?.map((tag) => {
          return { type: tag };
        });
        return tags ? tags : [{ type: ObjectTypeAPI.Generic }];
      },
      transformResponse: (
        response: any,
        _meta: any,
        args: IAgnosticGetQuery
      ) => {
        if (args?.params?.findOne) {
          return {
            ...response,
            data:
              Array.isArray(response.data) && response.data.length > 0
                ? response.data[0]
                : response.data,
          };
        }
        return response;
      },
    }),

    //DELETE
    agnosticDelete: builder.mutation<any, IAgnosticDeleteQuery>({
      query: ({ url, id, suffix = "", body }) => ({
        url: suffix == "" ? `${url}/${id}` : `${url}/${id}${suffix}`,
        method: "delete",
        body,
      }),
      invalidatesTags: (result) => {
        const tags = result?.tagsInvalidated?.map((tag) => {
          return { type: tag };
        });
        return tags ? tags : [{ type: ObjectTypeAPI.Generic }];
      },
    }),

    //Post
    agnosticPost: builder.mutation<any, IAgnosticPostQuery>({
      query: ({ url, body, method }) => ({
        body,
        url,
        method: method || "post", //if method is not provided, default to post, so it's post or put if passed
      }),
      //optimistic update
      // async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
      //   const patchResult = dispatch(
      //     agnosticAPI.util.updateQueryData('agnosticPost', undefined, (draft) => {
      //       Object.assign(draft, patch)
      //     })
      //   )
      //   try {
      //     await queryFulfilled
      //   } catch {
      //     patchResult.undo()
      //   }
      // },
      invalidatesTags: (result) => {
        const tags = result?.tagsInvalidated?.map((tag) => {
          return { type: tag };
        });
        return tags ? tags : [{ type: ObjectTypeAPI.Generic }];
      },
    }),
    agnosticPostMultiCall: builder.mutation<
      any,
      IAgnosticPostQuery & { calls: IMultiCallItem[] }
    >({
      query: ({ method, calls }) => ({
        body: {
          calls,
        },
        url: `/app/concurrent`,
        method: method || "post",
      }),
      invalidatesTags: (result) => {
        const tags = result?.tagsInvalidated?.map((tag) => {
          return { type: tag };
        });
        return tags ? tags : [{ type: ObjectTypeAPI.Generic }];
      },
      providesTags: (result) => {
        const tags = result?.tagsProvided?.map((tag) => {
          return { type: tag };
        });
        return tags ? tags : [{ type: ObjectTypeAPI.Generic }];
      },
    }),
  }),
});

export const {
  useAgnosticPostMultiCallMutation,
  useAgnosticUntaggedGetQuery,
  useLazyAgnosticUntaggedGetQuery,
  useAgnosticGetQuery,
  useLazyAgnosticGetQuery,
  useAgnosticPostMutation,
  useAgnosticDeleteMutation,
} = agnosticAPI;
